import { useMemo } from "react";
import { useBacklogItems } from "@/hooks/useBacklogItems";
import { useDropdownData } from "@/hooks/useDropdownData";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useMentions } from "@/hooks/useMentions";
import { isCompletionKind, statusKindLookup, type StatusKind } from "@/lib/statusKind";
import { parsePgArray } from "@/lib/pgArray";
import type { BacklogItem } from "@/types/backlog";

/**
 * "What should I do?" — the personal slice of the backlog, bucketed by
 * urgency. Shared by Home (which shows a capped preview of each bucket) and
 * the focused /focus/:group screens (which show a bucket in full), so the two
 * can never disagree about what is in a group.
 */
export const useMyWork = () => {
  const { data: items = [] } = useBacklogItems();
  const { data: dropdownData } = useDropdownData();
  const { userId } = useUserProfile();
  const { data: mentions = [] } = useMentions();

  const kindOf = useMemo(() => statusKindLookup(dropdownData?.statuses ?? []), [dropdownData?.statuses]);

  const live = useMemo(() => items.filter((i) => !i.deleted_at), [items]);

  /**
   * The work that is MINE — assigned to me, and nothing else.
   *
   * It used to also carry everything I had FILED, which made "What should I
   * do?" answer with a pile of tickets I had handed to other people. Filing a
   * ticket is a thing you did; being assigned one is a thing you owe. Only the
   * second belongs in a to-do list, and the first was the larger half for
   * anyone who triages.
   *
   * Unread mentions are still here (see below) and are the one exception on
   * purpose: a mention is somebody asking ME for something, which is the same
   * kind of debt as an assignment even though no ticket changed hands.
   *
   * Filed-by-me has not disappeared from the app — it is a facet of the
   * backlog now ("Added by" in the toolbar's Filter), which is where a
   * question about the whole list belongs.
   */
  const myItems = useMemo(() => {
    if (!userId) return [];
    return live.filter((i) => parsePgArray(i.assigned_to_id).includes(userId));
  }, [live, userId]);

  // Bucket by status kind. NULL kinds behave as plain active statuses (see
  // statusKind.ts), so they land in "In progress" rather than vanishing.
  const buckets = useMemo(() => {
    const blocked: BacklogItem[] = [];
    const todo: BacklogItem[] = [];
    const active: BacklogItem[] = [];
    const done: BacklogItem[] = [];
    for (const item of myItems) {
      const kind: StatusKind | null = kindOf(item.status_id);
      if (kind === "blocked") blocked.push(item);
      else if (kind === "todo" || kind === "on_hold") todo.push(item);
      else if (isCompletionKind(kind) || kind === "denied" || kind === "rejected") done.push(item);
      else active.push(item);
    }
    blocked.sort((a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime());
    const newestFirst = (a: BacklogItem, b: BacklogItem) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    todo.sort(newestFirst);
    active.sort(newestFirst);
    done.sort(newestFirst);
    return { blocked, todo, active, done };
  }, [myItems, kindOf]);

  const unreadMentions = useMemo(
    () => mentions.filter((m) => !m.is_read && m.comment?.backlog_item && !m.comment.backlog_item.deleted_at),
    [mentions],
  );

  // There was a `teamQueueItems` bucket here — unclaimed work in my teams'
  // queues, for "assign it to a team, someone picks it up later". Both of its
  // readers (Home's fourth tab and /focus/team) are gone: a ticket is assigned
  // to a person when it is filed in this workspace, so the bucket was always
  // empty. The team *queue* itself (`assigned_team_id`) is untouched — it is
  // only this derived list that had no one left to show it to.
  return { live, buckets, unreadMentions, kindOf };
};
